import { Button, Modal } from "flowbite-react";
import { useState } from "react";

/* ===================== Stage Timeline (Horizontal) – pretty + confirm ===================== */
export default function StageTimeline({
  stages = [],
  currentStageId,
  onClickStage,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingStage, setPendingStage] = useState(null);

  const currentIndexRaw = stages.findIndex((s) => s.id === currentStageId);
  const currentIndex = Math.max(0, currentIndexRaw);
  const total = Math.max(stages.length - 1, 1);
  const progressPct =
    stages.length > 1 && currentIndexRaw >= 0
      ? (currentIndex / total) * 100
      : 0;

  const requestChange = (stage) => {
    if (!stage || stage.id === currentStageId) return;
    setPendingStage(stage);
    setConfirmOpen(true);
  };

  const cancelMove = () => {
    setConfirmOpen(false);
    setPendingStage(null);
  };

  const confirmMove = () => {
    if (pendingStage) onClickStage?.(pendingStage.id);
    setConfirmOpen(false);
    setPendingStage(null);
  };

  const nextIndex = currentIndex + 1;

  return (
    <div className="mb-3 -mx-2 -mt-4 overflow-x-auto">
      <div className="min-w-[560px] rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="relative px-3 pb-2 pt-3">
          <div className="absolute left-6 right-6 top-6 h-1.5 rounded-full bg-slate-100" />
          <div
            className="absolute left-6 top-6 h-1.5 rounded-full bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-300 transition-all"
            style={{ width: `${progressPct}%` }}
          />

          <ol className="relative z-10 flex items-center justify-between gap-3">
            {stages.map((s, idx) => {
              const isActive = s.id === currentStageId;
              const isCompleted = currentIndexRaw >= 0 && idx < currentIndex;
              const isNext = idx === nextIndex;

              const bulletClasses = [
                "h-7 w-7 rounded-full border flex items-center justify-center text-[11px] font-semibold transition-all shadow-sm",
                isActive
                  ? "bg-slate-900 text-white border-gray-900 ring-4 ring-gray-300"
                  : isCompleted
                  ? "bg-emerald-500 text-white border-emerald-500 ring-4 ring-emerald-100"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
                isNext && !isActive
                  ? "outline outline-2 outline-slate-200"
                  : "",
              ].join(" ");

              const labelClasses = [
                "mt-2 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.2em] text-center truncate max-w-[108px]",
                isActive
                  ? "bg-slate-900 text-white"
                  : isCompleted
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-slate-50 text-slate-500",
              ].join(" ");

              return (
                <li
                  key={s.id}
                  className="flex flex-col items-center min-w-[90px]"
                >
                  <button
                    type="button"
                    onClick={() => requestChange(s)}
                    title={s.name}
                    className={bulletClasses}
                  >
                    {isCompleted ? (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 13l4 4L19 7"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </button>
                  <span className={labelClasses}>{s.name}</span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {/* Confirmação antes de mover */}
      <Modal show={confirmOpen} size="md" onClose={cancelMove}>
        <Modal.Header className="py-2 flex items-center">
          <span className="text-base font-medium">
            Confirmar mudança de etapa
          </span>
        </Modal.Header>
        <Modal.Body>
          {(() => {
            const from = stages[currentIndexRaw]?.name;
            const to = pendingStage?.name;
            const pendingIndex = stages.findIndex(
              (st) => st.id === pendingStage?.id
            );
            const jump =
              pendingIndex >= 0 && currentIndexRaw >= 0
                ? Math.abs(pendingIndex - currentIndexRaw)
                : 0;
            const goingForward = pendingIndex > currentIndexRaw;

            return (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  Mover o card de <b>{from || "—"}</b> para <b>{to || "—"}</b>?
                </p>
                {goingForward && jump > 1 && (
                  <p className="text-xs text-amber-600">
                    Aviso: isso vai pular {jump - 1} etapa(s).
                  </p>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="flex justify-end pt-2 pb-3">
          <Button color="gray" onClick={cancelMove}>
            Cancelar
          </Button>
          <Button onClick={confirmMove}>Mover</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
